import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { rideService } from '../../services/rideService';

const AdminRideManagement = () => {
  const [rides, setRides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchAllRides();
  }, []);

  const fetchAllRides = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase?.from('ride_offers')?.select(`
          *,
          driver:driver_id(full_name),
          event:event_id(title)
        `)?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setRides(data || []);
    } catch (err) {
      console.error('Error fetching rides:', err);
      setError(err?.message || 'Araç ilanları yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRide = async (rideId) => {
    try {
      await rideService?.cancelRide(rideId);
      setRides(rides?.filter(r => r?.id !== rideId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting ride:', err);
      alert(err?.message || 'İlan silinirken hata oluştu');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Belirtilmemiş';
    const date = new Date(dateString);
    return date?.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Araç Paylaşımı Yönetimi</h1>
          <p className="text-muted-foreground">Tüm araç paylaşım ilanlarını görüntüleyin ve yönetin</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-3 rounded-lg">
                <Icon name="Car" size={24} className="text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Toplam İlan</p>
                <p className="text-2xl font-bold text-foreground">{rides?.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="bg-success/10 p-3 rounded-lg">
                <Icon name="Users" size={24} className="text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Aktif Sürücü</p>
                <p className="text-2xl font-bold text-foreground">
                  {new Set(rides.map(r => r.driver_id))?.size}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3">
              <div className="bg-warning/10 p-3 rounded-lg">
                <Icon name="MapPin" size={24} className="text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Toplam Koltuk</p>
                <p className="text-2xl font-bold text-foreground">
                  {rides?.reduce((sum, r) => sum + (r?.available_seats || 0), 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">İlanlar yükleniyor...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-800">
              <Icon name="AlertCircle" size={20} />
              <p>{error}</p>
            </div>
          </div>
        )}

        {/* Table */}
        {!isLoading && !error && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Etkinlik
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sürücü
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nereden
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tarih
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Koltuk
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fiyat
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İşlem
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rides?.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <Icon name="Car" size={48} className="text-gray-300 mb-3" />
                          <p className="text-gray-500 font-medium">Henüz araç ilanı yok</p>
                          <p className="text-gray-400 text-sm">İlk ilan oluşturulduğunda burada görünecek</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    rides?.map((ride) => (
                      <tr key={ride?.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {ride?.event?.title || 'Bilinmeyen Etkinlik'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {ride?.driver?.full_name || 'Bilinmeyen Sürücü'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">
                            {ride?.origin_location || 'Belirtilmemiş'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(ride?.departure_time)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                            {ride?.available_seats || 0} koltuk
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {ride?.price_per_seat > 0 ? `₺${ride?.price_per_seat}` : 'Ücretsiz'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => setDeleteConfirm(ride?.id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          >
                            <Icon name="Trash2" size={16} />
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <Icon name="AlertTriangle" size={24} className="text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">İlanı Sil</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Bu araç ilanını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                İptal
              </button>
              <button
                onClick={() => handleDeleteRide(deleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRideManagement;